import type { InfiniteData } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import {
  type CommentCreateRequest,
  type CommentUpdateRequest,
  type CommunityFeedBulkViewRequest,
  type CommunityFeedItemV2DtoPaginationResultDto,
  type ManageReactionRequest,
  type MessageCreateRequest,
  type MessageDetailV2Dto,
  type MessageInformRequest,
  type MessageMultipleProjectsCreateRequest,
  type MessageReportRequest,
  type MessageStatusChangeRequest,
  type MessageStatusChangeRequestV2,
  type MessageUpdateRequest,
  type SurveyDetailsV2Dto,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useConfig } from "@/providers/config/useConfig";
import { QUERY_KEYS } from "@/query-keys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const useAddMessage = () => {
  const api = useApi();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ data }: { data: MessageCreateRequest }) => api.postMessagesV1(data).then((x) => x.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES(projectId) });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.message-feed.create.notifications.error") });
    },
  });
};

const useGetQuickReplyLink = () => {
  const api = useApi();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const dashboardUrl = useConfig("newDashboardRootUrl");

  return useMutation({
    mutationFn: (messageId: string) => api.postQuickReplyMessageCreateTokenV2({ messageId }).then((x) => x.data),
    onSuccess: async (token) => {
      const quickReplyMessagePath = routes.quickReply.message({ token: `V2-${token}` });

      await navigator.clipboard.writeText(`${dashboardUrl}${quickReplyMessagePath}`);

      showFlashToast({ variant: "success", message: t("component.community-post.quick-reply-link.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("component.community-post.quick-reply-link.error") });
    },
  });
};

const useDeleteMessage = () => {
  const api = useApi();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const sessionUser = useSessionUser();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({
      messageId,
      reason,
      details,
    }: { messageId: string } & Omit<MessageStatusChangeRequestV2, "newStatus">) =>
      api.postMessagesChangeStatusV2(messageId, { newStatus: "deleted", reason: reason, details: details }),
    onSuccess: (_, payload) => {
      showFlashToast({ variant: "success", message: t("component.community-post.delete.success") });

      if (sessionUser.isSuperAdmin) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, payload.messageId) });
      } else {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES(projectId) });
      }
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("component.community-post.delete.error") });
    },
  });
};

const useUpdateMessageStatus = () => {
  const api = useApi();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const sessionUser = useSessionUser();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ messageId, data }: { messageId: string; data: MessageStatusChangeRequestV2 }) =>
      api.postMessagesChangeStatusV2(messageId, data),
    onSuccess: (_, payload) => {
      let toastTitle = t("component.community-post.archive.success");
      if (payload.data.newStatus === "deleted") {
        toastTitle = t("component.community-post.delete.success");
      }

      showFlashToast({ variant: "success", message: toastTitle });

      if (sessionUser.isSuperAdmin) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, payload.messageId) });
      } else {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES(projectId) });
      }
    },
    onError: (_, payload) => {
      let toastTitle = t("component.community-post.archive.error");
      if (payload.data.newStatus === "deleted") {
        toastTitle = t("component.community-post.delete.error");
      }

      showFlashToast({ variant: "alert", message: toastTitle });
    },
  });
};

const useArchiveMessage = () => {
  const api = useApi();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const sessionUser = useSessionUser();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({
      messageId,
      reason,
      details,
    }: { messageId: string } & Omit<MessageStatusChangeRequest, "newStatus">) =>
      api.postMessagesChangeStatusV1(messageId, { newStatus: "archived", reason: reason, details: details }),
    onSuccess: (_, payload) => {
      showFlashToast({ variant: "success", message: t("component.community-post.archive.success") });

      if (sessionUser.isSuperAdmin) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, payload.messageId) });
      } else {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES(projectId) });
      }
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("component.community-post.archive.error") });
    },
  });
};

const useUpdateMessage = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ messageId, payload }: { messageId: string; payload: MessageUpdateRequest }) =>
      api.putMessagesV1(messageId, payload),
    onSuccess: (_, payload) => {
      showFlashToast({ variant: "success", message: t("page.message-feed.edit.notifications.success") });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, payload.messageId) });
    },
    onError() {
      showFlashToast({ variant: "alert", message: t("page.message-feed.edit.notifications.error") });
    },
  });
};

const useDeleteComment = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ messageId, commentId }: { messageId: string; commentId: string; pollId?: string }) =>
      api.deleteMessagesCommentsByIdV1(messageId, commentId).then((x) => x.data),
    onSuccess: (_, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, payload.messageId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_COMMENTS(projectId, payload.messageId) });
      if (payload.pollId) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POLL_DETAILS(projectId, payload.pollId) });
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES_FEED(projectId) });
      }
    },
    onError() {
      showFlashToast({ variant: "alert", message: t("component.community-post.comments.delete.error") });
    },
  });
};

const useAddComment = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({
      messageId,
      payload,
    }: {
      messageId: string;
      payload: CommentCreateRequest;
      failureMessage: string;
      pollId?: string;
    }) => api.postMessagesCommentsV1(messageId, payload).then((x) => x.data),
    onSuccess: (_, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, payload.messageId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_COMMENTS(projectId, payload.messageId) });
      if (payload.pollId) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POLL_DETAILS(projectId, payload.pollId) });
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES_FEED(projectId) });
      }
    },
    onError(_, { failureMessage }) {
      showFlashToast({ variant: "alert", message: failureMessage });
    },
  });
};

const useUpdateComment = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({
      messageId,
      payload,
      commentId,
    }: {
      messageId: string;
      payload: CommentUpdateRequest;
      commentId: string;
      failureMessage: string;
    }) => api.putMessagesCommentsV1(messageId, commentId, payload).then((x) => x.data),
    onSuccess: (_, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, payload.messageId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_COMMENTS(projectId, payload.messageId) });
    },
    onError(_, { failureMessage }) {
      showFlashToast({ variant: "alert", message: failureMessage });
    },
  });
};

const useUpdateMessageReaction = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ messageId, data }: { messageId: string; data: ManageReactionRequest; pollId?: string }) =>
      api.putMessagesReactV1(messageId, data),
    onSuccess: (_, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, payload.messageId) });
      if (payload.pollId) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POLL_DETAILS(projectId, payload.pollId) });
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES_FEED(projectId) });
      }
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("component.community-post.like.error") });
    },
  });
};

const useUpdateCommentReaction = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      messageId,
      commentId,
      data,
    }: {
      messageId: string;
      commentId: string;
      data: ManageReactionRequest;
    }) => api.putMessagesCommentsReactV1(messageId, commentId, data),
    onSuccess: (_, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_COMMENTS(projectId, payload.messageId) });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("component.community-post.comment.like.error") });
    },
  });
};

const useAddMassMessage = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { data: MessageMultipleProjectsCreateRequest }) =>
      api.postMassMessagesV1(payload.data).then((x) => x.data),
    onSuccess: (_, payload) => {
      payload.data.projectSelected.forEach((projectId) => {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES_FEED(projectId) });
      });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.message-feed.create.notifications.error") });
    },
  });
};

const useAddViewCount = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { data: CommunityFeedBulkViewRequest }) => api.postCommunityFeedViewV2(payload.data),
    onMutate: (payload) => {
      // Optimistically update the tracking state for community feed data
      // Only marking the item as tracked, don't care about the actual view count for now
      queryClient.setQueryData<InfiniteData<CommunityFeedItemV2DtoPaginationResultDto>>(
        QUERY_KEYS.MESSAGES_FEED(projectId),
        (currData) => {
          if (!currData) return currData;

          const newData = structuredClone(currData);
          for (const page of newData.pages) {
            for (const item of page.items) {
              if (item.type === "message" && item.message && payload.data.viewedPosts?.includes(item.message.id)) {
                item.message.viewTracked = true;
              }

              if (item.type === "poll" && item.poll && payload.data.viewedPolls?.includes(item.poll.id)) {
                item.poll.viewTracked = true;
              }

              if (item.type === "survey" && item.survey && payload.data.viewedSurveys?.includes(item.survey.id)) {
                item.survey.viewTracked = true;
              }
            }
          }

          return newData;
        },
      );

      for (const viewedPostId of payload.data.viewedPosts || []) {
        const queryKey = QUERY_KEYS.MESSAGE_DETAILS(projectId, viewedPostId);
        queryClient.setQueryData<MessageDetailV2Dto>(queryKey, (currData) => {
          if (!currData) return currData;

          return {
            ...currData,
            viewTracked: true,
          };
        });
      }

      for (const viewedSurveyId of payload.data.viewedSurveys || []) {
        const queryKey = QUERY_KEYS.SURVEY_DETAILS(projectId, viewedSurveyId);
        queryClient.setQueryData<SurveyDetailsV2Dto>(queryKey, (currData) => {
          if (!currData) return currData;

          return {
            ...currData,
            viewTracked: true,
          };
        });
      }
    },
  });
};

const useMarkMessageAsRead = (messageId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => api.postMessagesReadV1(messageId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, messageId) });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES_FEED_STATS(projectId) });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("component.community-post.mark-as-read.error") });
    },
  });
};

const useReportMessage = (messageId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: MessageReportRequest) => api.postMessagesReportV1(messageId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, messageId) });
      showFlashToast({ variant: "success", message: t("component.community-post.report.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("component.community-post.report.error") });
    },
  });
};

const useInformAdmins = (messageId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: MessageInformRequest) => api.postMessagesInformsV1(messageId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, messageId) });
      queryClient.removeQueries({ queryKey: QUERY_KEYS.MESSAGE_INFORMED_ADMINS(projectId, messageId) });
      showFlashToast({ variant: "success", message: t("component.community-post.inform-admin.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("component.community-post.inform-admin.error") });
    },
  });
};

export const communityFeedMutations = {
  useAddMessage,
  useAddMassMessage,
  useUpdateMessage,
  useArchiveMessage,
  useDeleteMessage,
  useUpdateMessageStatus,
  useGetQuickReplyLink,
  useDeleteComment,
  useAddComment,
  useUpdateComment,
  useUpdateMessageReaction,
  useUpdateCommentReaction,
  useAddViewCount,
  useMarkMessageAsRead,
  useReportMessage,
  useInformAdmins,
};
